import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    ChevronRight, Mail, Home, CreditCard,
    Wrench, MessageSquare, Send, Loader2, CheckCircle2, Clock, AlertCircle,
} from 'lucide-react';
import { useAppContext } from '../context/useAppContext';
import { supabase } from '../lib/supabase';
import { formatDate, formatCurrency } from '../utils/formatters';

const ACCENT = '#16a34a';
const ACCENT_LIGHT = 'rgba(22,163,74,0.09)';

const card: React.CSSProperties = {
    background: '#fff', borderRadius: '20px', padding: '1.75rem',
    border: '1px solid #f1f5f9',
    boxShadow: '0 1px 4px rgba(0,0,0,0.03), 0 4px 16px rgba(0,0,0,0.03)',
};

export default function TenantProfilePage() {
    const { id } = useParams<{ id: string }>();
    const { user } = useAppContext();

    const [tenant, setTenant] = useState<any>(null);
    const [property, setProperty] = useState<any>(null);
    const [payments, setPayments] = useState<any[]>([]);
    const [tickets, setTickets] = useState<any[]>([]);
    const [notes, setNotes] = useState<any[]>([]);
    const [noteText, setNoteText] = useState('');
    const [loading, setLoading] = useState(true);
    const [submittingNote, setSubmittingNote] = useState(false);

    useEffect(() => {
        if (!id || !user.id) return;
        loadAll();
    }, [id, user.id]);

    const loadAll = async () => {
        setLoading(true);
        try {
            // 1. Fetch tenant link
            const { data: tl } = await supabase
                .from('tenant_links')
                .select('*')
                .eq('id', id)
                .maybeSingle();
            if (!tl) { setLoading(false); return; }
            setTenant(tl);

            // 2. Fetch property
            const { data: prop } = await supabase
                .from('properties')
                .select('*')
                .eq('id', tl.property_id)
                .maybeSingle();
            setProperty(prop);

            // 3. Fetch payments (rent_payments table)
            const { data: pays } = await supabase
                .from('rent_payments')
                .select('*')
                .eq('property_id', tl.property_id)
                .order('due_date', { ascending: false })
                .limit(12);
            setPayments(pays || []);

            // 4. Fetch maintenance tickets for this property
            const { data: tix } = await supabase
                .from('maintenance_tickets')
                .select('*')
                .eq('property_id', tl.property_id)
                .order('created_at', { ascending: false })
                .limit(10);
            setTickets(tix || []);

            // 5. Fetch landlord notes (from ticket_comments as a notes system)
            // Using ticket_comments table but with a special null ticket_id sentinel
            // Actually load from a landlord_notes table if it exists, otherwise skip
            setNotes([]);
        } catch (err) {
            console.error('Error loading tenant profile:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSendNote = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!noteText.trim()) return;
        setSubmittingNote(true);
        // Store as a local note for now (append to notes array)
        setNotes(prev => [...prev, {
            id: Date.now().toString(),
            content: noteText.trim(),
            author_name: user.name || 'Landlord',
            created_at: new Date().toISOString(),
        }]);
        setNoteText('');
        setSubmittingNote(false);
    };

    if (loading) {
        return (
            <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <Loader2 className="animate-spin" size={40} color={ACCENT} />
            </div>
        );
    }

    if (!tenant) {
        return (
            <div className="page">
                <div className="breadcrumb">
                    <Link to="/app/landlord/tenants">Tenants</Link>
                    <ChevronRight size={14} />
                    <span>Not Found</span>
                </div>
                <div style={{ padding: '4rem', textAlign: 'center' }}>
                    <p style={{ color: 'var(--color-text-secondary)' }}>Tenant not found.</p>
                </div>
            </div>
        );
    }

    const initials = tenant.tenant_name?.substring(0, 2).toUpperCase() || '??';
    const isActive = tenant.invite_status === 'active';
    const openTickets = tickets.filter((t: any) => t.status !== 'resolved').length;
    const paidPayments = payments.filter((p: any) => p.status === 'paid').length;

    return (
        <div className="page dashboard-page">
            {/* Breadcrumb */}
            <div className="breadcrumb" style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9375rem', fontWeight: 500, color: 'var(--color-text-tertiary)' }}>
                <Link to="/app/landlord/tenants" style={{ color: 'inherit', textDecoration: 'none' }}>Tenants</Link>
                <ChevronRight size={14} />
                <span style={{ color: '#1a1d2e', fontWeight: 700 }}>{tenant.tenant_name}</span>
            </div>

            {/* Hero header */}
            <div style={{ background: 'linear-gradient(135deg, #0f1c12 0%, #0a1a0d 100%)', borderRadius: '24px', padding: '2.5rem', marginBottom: '2rem', color: '#fff', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap' as const }}>
                    <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #16a34a, #4ade80)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.75rem', fontWeight: 900, color: '#fff', flexShrink: 0 }}>
                        {initials}
                    </div>
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                            <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 900, letterSpacing: '-0.04em' }}>{tenant.tenant_name}</h1>
                            <span style={{ padding: '4px 14px', borderRadius: 99, fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.06em', background: isActive ? 'rgba(74,222,128,0.18)' : 'rgba(250,204,21,0.18)', color: isActive ? '#4ade80' : '#fbbf24', border: '1px solid currentColor' }}>
                                {isActive ? 'ACTIVE' : 'INVITED'}
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,0.6)', fontSize: '0.9375rem' }}>
                            <Mail size={15} />
                            <span>{tenant.tenant_email}</span>
                        </div>
                    </div>
                    {/* Stats bar */}
                    <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' as const }}>
                        {[
                            { label: 'Payments Made', value: paidPayments },
                            { label: 'Open Tickets', value: openTickets },
                            { label: 'Notes', value: notes.length },
                        ].map(s => (
                            <div key={s.label} style={{ textAlign: 'center' as const }}>
                                <div style={{ fontSize: '1.875rem', fontWeight: 900, letterSpacing: '-0.04em', color: '#4ade80' }}>{s.value}</div>
                                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, marginTop: '2px' }}>{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <Home size={120} style={{ position: 'absolute', bottom: '-20px', right: '20px', opacity: 0.05, transform: 'rotate(-5deg)' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                {/* Contact & Lease */}
                <section style={card}>
                    <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 1.25rem 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ background: ACCENT_LIGHT, padding: '6px', borderRadius: '8px', color: ACCENT, display: 'flex' }}><Mail size={15} /></span>
                        Contact & Lease
                    </h2>
                    {[
                        { label: 'Full Name', value: tenant.tenant_name },
                        { label: 'Email', value: tenant.tenant_email },
                        { label: 'Invite Code', value: tenant.invite_code, mono: true },
                        { label: 'Lease Start', value: tenant.lease_start ? formatDate(tenant.lease_start) : '—' },
                        { label: 'Lease End', value: tenant.lease_end ? formatDate(tenant.lease_end) : 'Ongoing' },
                    ].map(row => (
                        <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.625rem 0', borderBottom: '1px solid #f8fafc' }}>
                            <span style={{ fontSize: '0.8125rem', color: '#94a3b8', fontWeight: 600 }}>{row.label}</span>
                            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1a1d2e', fontFamily: row.mono ? 'monospace' : 'inherit' }}>{row.value}</span>
                        </div>
                    ))}
                </section>

                {/* Property */}
                <section style={card}>
                    <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 1.25rem 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ background: 'rgba(59,130,246,0.09)', padding: '6px', borderRadius: '8px', color: '#2563eb', display: 'flex' }}><Home size={15} /></span>
                        Property
                    </h2>
                    {property ? (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.625rem 0', borderBottom: '1px solid #f8fafc' }}>
                                <span style={{ fontSize: '0.8125rem', color: '#94a3b8', fontWeight: 600 }}>Name</span>
                                <Link to={`/app/landlord/properties/${property.id}`} style={{ fontSize: '0.9rem', fontWeight: 700, color: ACCENT, textDecoration: 'none' }}>{property.name}</Link>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.625rem 0', borderBottom: '1px solid #f8fafc' }}>
                                <span style={{ fontSize: '0.8125rem', color: '#94a3b8', fontWeight: 600 }}>Address</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1a1d2e', textAlign: 'right' as const, maxWidth: '60%' }}>{property.address || '—'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.625rem 0', borderBottom: '1px solid #f8fafc' }}>
                                <span style={{ fontSize: '0.8125rem', color: '#94a3b8', fontWeight: 600 }}>Monthly Rent</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: 800, color: ACCENT }}>{formatCurrency(property.rent_amount)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.625rem 0' }}>
                                <span style={{ fontSize: '0.8125rem', color: '#94a3b8', fontWeight: 600 }}>Due Day</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1a1d2e' }}>Day {property.due_day}</span>
                            </div>
                        </>
                    ) : (
                        <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Property data unavailable.</p>
                    )}
                </section>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                {/* Payment history */}
                <section style={{ ...card, padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '1.5rem 1.75rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ background: 'rgba(245,158,11,0.09)', padding: '6px', borderRadius: '8px', color: '#d97706', display: 'flex' }}><CreditCard size={15} /></span>
                            Payment History
                        </h2>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600 }}>{payments.length} records</span>
                    </div>
                    {payments.length === 0 ? (
                        <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>
                            <CreditCard size={32} opacity={0.2} style={{ marginBottom: '0.75rem' }} />
                            <p style={{ margin: 0, fontSize: '0.875rem' }}>No payment records found.</p>
                        </div>
                    ) : (
                        <div style={{ maxHeight: 320, overflow: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc' }}>
                                        <th style={{ padding: '0.875rem 1.75rem', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', textAlign: 'left' }}>Period</th>
                                        <th style={{ padding: '0.875rem 1.75rem', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', textAlign: 'left' }}>Amount</th>
                                        <th style={{ padding: '0.875rem 1.75rem', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', textAlign: 'left' }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payments.map((p: any) => (
                                        <tr key={p.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                                            <td style={{ padding: '1rem 1.75rem', fontSize: '0.875rem', fontWeight: 600, color: '#1a1d2e' }}>{p.period || formatDate(p.due_date)}</td>
                                            <td style={{ padding: '1rem 1.75rem', fontSize: '0.875rem', fontWeight: 700, color: ACCENT }}>{formatCurrency(p.amount)}</td>
                                            <td style={{ padding: '1rem 1.75rem' }}>
                                                <span style={{ padding: '4px 10px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: 800, background: p.status === 'paid' ? 'rgba(34,197,94,0.1)' : p.status === 'overdue' ? 'rgba(239,68,68,0.1)' : 'rgba(234,179,8,0.1)', color: p.status === 'paid' ? '#16a34a' : p.status === 'overdue' ? '#ef4444' : '#ca8a04', textTransform: 'uppercase' as const }}>
                                                    {p.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                {/* Maintenance tickets */}
                <section style={{ ...card, padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '1.5rem 1.75rem', borderBottom: '1px solid #f1f5f9' }}>
                        <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ background: 'rgba(239,68,68,0.09)', padding: '6px', borderRadius: '8px', color: '#ef4444', display: 'flex' }}><Wrench size={15} /></span>
                            Maintenance
                        </h2>
                    </div>
                    {tickets.length === 0 ? (
                        <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>
                            <Wrench size={32} opacity={0.2} style={{ marginBottom: '0.75rem' }} />
                            <p style={{ margin: 0, fontSize: '0.875rem' }}>No maintenance requests.</p>
                        </div>
                    ) : (
                        <div style={{ maxHeight: 320, overflow: 'auto' }}>
                            {tickets.map((t: any) => (
                                <div key={t.id} style={{ padding: '1rem 1.75rem', borderBottom: '1px solid #f8fafc', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ flexShrink: 0 }}>
                                        {t.status === 'resolved'
                                            ? <CheckCircle2 size={16} color="#16a34a" />
                                            : t.status === 'in_progress'
                                            ? <Clock size={16} color="#d97706" />
                                            : <AlertCircle size={16} color="#ef4444" />}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#1a1d2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.title}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>{formatDate(t.created_at)}</div>
                                    </div>
                                    <span style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '0.65rem', fontWeight: 800, background: t.priority === 'high' ? 'rgba(239,68,68,0.1)' : t.priority === 'medium' ? 'rgba(245,158,11,0.1)' : 'rgba(107,114,128,0.1)', color: t.priority === 'high' ? '#ef4444' : t.priority === 'medium' ? '#f59e0b' : '#6b7280', flexShrink: 0, textTransform: 'uppercase' as const }}>
                                        {t.priority}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {/* Notes / Communication */}
            <section style={{ ...card }}>
                <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 1.25rem 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ background: 'rgba(99,102,241,0.09)', padding: '6px', borderRadius: '8px', color: '#6366f1', display: 'flex' }}><MessageSquare size={15} /></span>
                    Landlord Notes
                    <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>Private notes visible only to you</span>
                </h2>
                <div style={{ marginBottom: '1.25rem', display: 'flex', flexDirection: 'column', gap: '10px', minHeight: notes.length === 0 ? '60px' : 'auto' }}>
                    {notes.length === 0 ? (
                        <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.875rem' }}>No notes yet. Add a note about this tenant below.</p>
                    ) : (
                        notes.map(n => (
                            <div key={n.id} style={{ background: '#f8fafc', borderRadius: '12px', padding: '1rem 1.25rem', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                    <span style={{ fontWeight: 700, fontSize: '0.8125rem', color: '#1a1d2e' }}>{n.author_name}</span>
                                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{formatDate(n.created_at)}</span>
                                </div>
                                <p style={{ margin: 0, fontSize: '0.9rem', color: '#475569', lineHeight: 1.55 }}>{n.content}</p>
                            </div>
                        ))
                    )}
                </div>
                <form onSubmit={handleSendNote} style={{ display: 'flex', gap: '10px' }}>
                    <input
                        type="text"
                        value={noteText}
                        onChange={e => setNoteText(e.target.value)}
                        placeholder="Add a private note about this tenant..."
                        style={{ flex: 1, padding: '0.75rem 1rem', borderRadius: '12px', border: '1.5px solid #e8ecf0', background: '#f8fafc', fontSize: '0.9375rem', fontWeight: 500, color: '#1a1d2e', outline: 'none' }}
                    />
                    <button type="submit" disabled={!noteText.trim() || submittingNote} style={{ padding: '0.75rem 1.25rem', borderRadius: '12px', border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '0.875rem', opacity: !noteText.trim() ? 0.5 : 1 }}>
                        {submittingNote ? <Loader2 size={16} className="animate-spin" /> : <><Send size={16} /> Save Note</>}
                    </button>
                </form>
            </section>
        </div>
    );
}
